/* Storewide-sale price display. Loaded globally from theme.liquid (not from a
   section) because script tags inside SPA-swapped content never execute — the
   same reason od-resume-row.js is global.

   Fetches the active sales from the membership app (/apps/library/active-sale,
   the same discount records the checkout Function reads, so displayed and
   charged prices always agree) and decorates every price element that
   snippets/_price-element.liquid tagged with data-od-sale-base (cents).

   MULTI-SALE: several sales can run at once (each scoped to its own
   collections). Every element is matched against all live sales and takes
   the best-percentage sale that covers it; a storewide sale (no collections)
   covers everything.

   Decoration is non-destructive: the original price span is struck via a CSS
   class and a sale-price span is inserted before it, so Alpine bindings on
   the original nodes keep working. A MutationObserver re-applies after SPA
   swaps, Alpine re-renders, and PDP variant switches. FLAC UPGRADE variants
   (data-od-sale-excluded) are never decorated, matching the Function's
   FLACUP SKU exclusion. */
(function () {
  "use strict";

  var ENDPOINT = "/apps/library/active-sale";
  var CACHE_KEY = "od-active-sale-v3";
  var CACHE_TTL_MS = 5 * 60 * 1000;

  var sales = [];
  var membershipDiscounts = {};
  var memberTier = "";
  var fetchPromise = null;

  var TIER_ENDPOINT = "/apps/library/membership/me";
  var TIER_CACHE_KEY = "od-member-tier-v1";
  var TIER_CACHE_TTL_MS = 5 * 60 * 1000;

  /* The customer's own tier, from the app rather than from Liquid.
     A customer metafield is only readable in Liquid if its definition exposes
     it to the storefront, and membership_tier is not, so the theme cannot see
     it however it is asked. The app reads it through the Admin API.

     Only for signed in visitors, and never cached beyond this browser: it is
     one person's tier. */
  function loadMemberTier() {
    if (!window.ODCustomerSignedIn) return Promise.resolve("");

    try {
      var raw = sessionStorage.getItem(TIER_CACHE_KEY);
      if (raw) {
        var parsed = JSON.parse(raw);
        if (parsed && Date.now() - parsed.at < TIER_CACHE_TTL_MS) {
          memberTier = parsed.tier || "";
          return Promise.resolve(memberTier);
        }
      }
    } catch (error) {
      /* private mode, or nothing stored */
    }

    return fetch(TIER_ENDPOINT, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(function (response) { return response.json(); })
      .then(function (payload) {
        memberTier = (payload && payload.tier) || "";
        try {
          sessionStorage.setItem(
            TIER_CACHE_KEY,
            JSON.stringify({ at: Date.now(), tier: memberTier })
          );
        } catch (error) {
          /* not worth failing over */
        }
        return memberTier;
      })
      .catch(function () {
        // Standard prices. Showing a discount we could not confirm would
        // promise a price the checkout might not honour.
        memberTier = "";
        return memberTier;
      });
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.at > CACHE_TTL_MS) return null;
      if (!Array.isArray(parsed.sales)) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  }

  function writeCache(value, discounts) {
    try {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ at: Date.now(), sales: value, discounts: discounts })
      );
    } catch (error) {
      /* private mode etc. — fetch again next page */
    }
  }

  function saleIsLive(value) {
    if (!value) return false;
    if (value.endsAt && Date.parse(value.endsAt) <= Date.now()) return false;
    var percentage = Number(value.percentage);
    return isFinite(percentage) && percentage > 0 && percentage <= 100;
  }

  function normalizeSales(payload) {
    if (!payload || !payload.ok) return [];
    var list = Array.isArray(payload.sales)
      ? payload.sales
      : payload.sale
        ? [payload.sale]
        : [];
    return list.filter(saleIsLive);
  }

  function loadSales() {
    if (fetchPromise) return fetchPromise;

    var cached = readCache();
    if (cached) {
      sales = cached.sales.filter(saleIsLive);
      membershipDiscounts = cached.discounts || {};
      fetchPromise = Promise.resolve(sales);
      return fetchPromise;
    }

    fetchPromise = fetch(ENDPOINT, { headers: { Accept: "application/json" } })
      .then(function (response) {
        if (!response.ok) throw new Error("active-sale " + response.status);
        return response.json();
      })
      .then(function (payload) {
        sales = normalizeSales(payload);
        membershipDiscounts =
          payload && payload.membershipDiscounts ? payload.membershipDiscounts : {};
        writeCache(sales, membershipDiscounts);
        return sales;
      })
      .catch(function () {
        sales = [];
        return sales;
      });

    return fetchPromise;
  }

  function formatPrice(cents) {
    try {
      if (window.Alpine && window.Alpine.store) {
        var main = window.Alpine.store("main");
        if (main && typeof main.getPrice === "function") {
          return main.getPrice(cents);
        }
      }
    } catch (error) {
      /* fall through to the manual format */
    }
    return "$" + (cents / 100).toFixed(2).replace(/\.00$/, "");
  }

  function salePriceCents(baseCents, percentage) {
    return Math.round(baseCents * (1 - percentage / 100));
  }

  function saleCovers(saleValue, el) {
    if (!saleValue.collections || !saleValue.collections.length) return true;
    // Collection-scoped sale: only decorate elements that declare a matching
    // collection handle. Elements without the attribute (e.g. Alpine-rendered
    // popups, which lack collection data) stay undecorated — checkout still
    // charges correctly either way.
    var attr = el.dataset.odSaleCollections;
    if (!attr) return false;
    var handles = attr.split(",");
    for (var i = 0; i < saleValue.collections.length; i++) {
      if (handles.indexOf(saleValue.collections[i].handle) !== -1) return true;
    }
    return false;
  }

  /* The signed in customer's own membership discount, as one more sale.
     window.ODMembershipTier is rendered by Liquid on every page; the tier to
     percentage map is public and identical for everybody, so pairing them here
     keeps the sale endpoint cacheable.

     Treating it as a sale is what makes the display match the checkout: the
     discount is not combinable, so Shopify charges whichever is better, and
     the same best-percentage rule decides what is shown. */
  function membershipSaleFor() {
    var tier = String(memberTier || window.ODMembershipTier || "").toLowerCase();
    if (!tier || tier === "non_member") return null;

    var percentage = Number(membershipDiscounts[tier]);
    if (!isFinite(percentage) || percentage <= 0 || percentage > 100) return null;

    return { percentage: percentage, collections: [], membership: true };
  }

  // Best sale for this element: highest percentage among the sales that
  // cover it (sales never overlap by admin-page rule, but be safe anyway).
  function saleFor(el) {
    var best = null;
    for (var i = 0; i < sales.length; i++) {
      if (!saleCovers(sales[i], el)) continue;
      if (!best || Number(sales[i].percentage) > Number(best.percentage)) {
        best = sales[i];
      }
    }

    var member = membershipSaleFor();
    if (member && (!best || member.percentage > Number(best.percentage))) {
      best = member;
    }

    return best;
  }

  function tierHint(saleValue) {
    if (!saleValue.tiers || !saleValue.tiers.length) return "";
    return saleValue.tiers
      .map(function (tier) {
        return "$" + tier.minSubtotal + "+: " + tier.percentage + "% off";
      })
      .join(", ");
  }

  function decorate(el) {
    if (el.dataset.odSaleExcluded === "1") return;
    var saleValue = saleFor(el);
    if (!saleValue) return;

    var baseCents = Number(el.dataset.odSaleBase);
    if (!isFinite(baseCents) || baseCents <= 0) return;

    // Collection cards mark their price text with data-od-price-current;
    // _price-element output marks it with the theme's price color class.
    var priceSpan =
      el.querySelector("[data-od-price-current]") ||
      el.querySelector(".text-primary-600");
    if (!priceSpan) return;

    var saleCents = salePriceCents(baseCents, Number(saleValue.percentage));
    if (saleCents >= baseCents) return;

    // Cards with a price range (data-od-sale-max) show "low - high" and get a
    // discounted range in return.
    var maxCents = Number(el.dataset.odSaleMax);
    var isRange = isFinite(maxCents) && maxCents > baseCents;
    var saleText = formatPrice(saleCents);
    if (isRange) {
      saleText += " - " + formatPrice(salePriceCents(maxCents, Number(saleValue.percentage)));
    }

    var stamp = saleValue.percentage + ":" + baseCents + ":" + (maxCents || 0);
    if (el.dataset.odSaleApplied === stamp) return;
    el.dataset.odSaleApplied = stamp;

    var saleSpan = el.querySelector(".od-sale-display__price");
    if (!saleSpan) {
      saleSpan = document.createElement("span");
      saleSpan.className = "text-primary-600 od-sale-display__price";
      priceSpan.parentNode.insertBefore(saleSpan, priceSpan);
    }
    saleSpan.textContent = saleText;

    // A struck single price reads fine ($12.57 $17.95), but a struck RANGE
    // next to a sale range is four numbers of noise — the -XX% OFF badge
    // carries the "was" story on cards, so ranges just swap to sale prices.
    if (isRange) {
      priceSpan.classList.add("od-sale-display__hidden");
    } else {
      priceSpan.classList.add("od-sale-display__struck");
    }

    // A pre-existing compare-at strike would now read as two struck prices;
    // hide it while the storewide sale is on.
    var compare = el.querySelector("s:not(.od-sale-display__struck)");
    if (compare) compare.classList.add("od-sale-display__hidden");

    labelMembership(el, saleValue);
  }

  function titleCase(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  /* Names the membership discount, on the product page only.
     A pill on every card would shout about the smallest saving on the site, but
     on the page where somebody is deciding, an unexplained lower price raises a
     question. .od-product-price wraps the product page's own price block and
     nothing else, so related-product cards on the same page stay unlabelled. */
  function labelMembership(el, saleValue) {
    var holder = el.closest(".od-product-price");
    if (!holder) return;

    var existing = holder.querySelector(".od-sale-display__member-note");

    if (!saleValue || !saleValue.membership) {
      if (existing) existing.remove();
      return;
    }

    var tier = String(memberTier || window.ODMembershipTier || "").toLowerCase();
    if (!tier) return;

    var text =
      formatPercentValue(saleValue.percentage) + "% " + titleCase(tier) + " discount";

    if (existing) {
      existing.textContent = text;
      return;
    }

    var note = document.createElement("span");
    note.className = "od-sale-display__member-note";
    note.textContent = text;
    el.appendChild(note);
  }

  function formatPercentValue(percentage) {
    var value = Number(percentage);
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }

  function formatPercentOff(percentage) {
    var value = Number(percentage);
    var text = Number.isInteger(value) ? String(value) : value.toFixed(2);
    return "-" + text + "% OFF";
  }

  // Fills and reveals the theme's own badge pill (rendered hidden by
  // snippets/_discount-badge.liquid) wherever a sale covers the product.
  function decorateBadge(el) {
    if (el.dataset.odSaleExcluded === "1") return;
    var saleValue = saleFor(el);
    if (!saleValue) return;

    /* No badge for a membership discount. A five percent pill on every price
       in the shop shouts about the smallest saving on the site and cheapens
       the sales that are worth announcing. The discounted price is enough. */
    if (saleValue.membership) return;

    var stamp = String(saleValue.percentage);
    if (el.dataset.odSaleApplied === stamp) return;
    el.dataset.odSaleApplied = stamp;

    var label = el.querySelector("span");
    if (!label) return;

    var text = formatPercentOff(saleValue.percentage);
    // Inline PDP badge (inside the price row) has room for the tier hint;
    // card overlays stay short.
    var hint = tierHint(saleValue);
    if (hint && el.closest("[data-od-sale-base]")) {
      text += " · " + hint;
    }
    label.textContent = text;

    // A compare-at badge in the same slot would overlap the revealed pill.
    var parent = el.parentElement;
    if (parent) {
      var siblings = parent.querySelectorAll(
        ".card-product__badge-container:not([data-od-sale-pct-badge])"
      );
      for (var i = 0; i < siblings.length; i++) {
        siblings[i].classList.add("od-sale-display__hidden");
      }
    }

    el.hidden = false;
  }

  function decorateAll(root) {
    if (!sales.length) return;
    var scope = root && root.querySelectorAll ? root : document;
    var elements = scope.querySelectorAll("[data-od-sale-base]");
    for (var i = 0; i < elements.length; i++) {
      decorate(elements[i]);
    }
    var badges = scope.querySelectorAll("[data-od-sale-pct-badge]");
    for (var j = 0; j < badges.length; j++) {
      decorateBadge(badges[j]);
    }
    if (scope !== document && scope.matches && scope.matches("[data-od-sale-base]")) {
      decorate(scope);
    }
    if (scope !== document && scope.matches && scope.matches("[data-od-sale-pct-badge]")) {
      decorateBadge(scope);
    }
  }

  var observer = null;

  function arm() {
    if (observer) return;
    observer = new MutationObserver(function (mutations) {
      if (!sales.length) return;
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (mutation.type === "attributes") {
          // Alpine updated a bound data-od-sale-base — re-decorate that node.
          delete mutation.target.dataset.odSaleApplied;
          decorate(mutation.target);
        } else {
          for (var j = 0; j < mutation.addedNodes.length; j++) {
            var node = mutation.addedNodes[j];
            if (node.nodeType === 1) decorateAll(node);
          }
        }
      }
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-od-sale-base"],
    });
  }

  function boot() {
    // Both together: a member's price is not a sale, so waiting for one would
    // mean member prices only appeared on days something was on sale.
    Promise.all([loadSales(), loadMemberTier()]).then(function () {
      // Nothing to decorate only when there is neither a sale nor a membership
      // discount. The old test was sales alone, which meant a Patron saw
      // standard prices whenever no sale was running.
      if (!sales.length && !membershipSaleFor()) return;
      decorateAll(document);
      arm();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
